import { AuctionState } from '../../../manage/shared/models'
import { AuctionStatus } from '../../../../core/constants'
import { StringHelper } from './StringHelper'
import { LotHelper } from './LotHelper'
import { AUCTION_FORMAT } from '../constants/constants'
import { SaleyardAuction } from '../models/saleyard-auction-get-all-response'
import { Bid } from '../../../manage/shared/models/bid.model'
import { AuctionModel } from '../../../public/models/auction.model'
import objectPath from 'object-path'
import { Bidder } from '../../shared/models/bidder.model'

export class AuctionHelper {
    static getAuctionDescriptionText(auction: SaleyardAuction, datePipe) {
        let strings = [auction.id, auction.title]
        if (auction.saleyard) {
            strings.push(auction.saleyard.name)
        }
        strings.push(datePipe.transform(auction.startsAt))
        return strings.join(', ')
    }

    static getStatusName(stateNumber: number) {
        return StringHelper.camelCaseToSpaces(
            stateNumber
                ? Object.keys(AuctionStatus)
                      .map(key => ({ id: Number(key), name: AuctionStatus[key] }))
                      .find(x => x.id === stateNumber)!.name
                : ''
        )
    }

    static isBeforeOrAfterAuction(auctionState: AuctionState) {
        return AuctionHelper.isBeforeAuction(auctionState) || AuctionHelper.isAfterAuction(auctionState)
    }

    static isBeforeAuction(auctionState: AuctionState) {
        return AuctionHelper.isAuctionReady(auctionState) || AuctionHelper.isAuctionFinalised(auctionState)
    }

    static isAfterAuction(auctionState: AuctionState) {
        return AuctionHelper.isAuctionFinished(auctionState)
    }

    static isAuctionFinalised(auctionState: AuctionState | null) {
        return auctionState && [AuctionStatus.Finalised].includes(auctionState.auctionStatus!)
    }

    static isAuctionReady(auctionState: AuctionState) {
        return auctionState && [AuctionStatus.Ready].includes(auctionState.auctionStatus!)
    }

    static isAuctionPaused(auctionState: AuctionState) {
        return auctionState && [AuctionStatus.Paused].includes(auctionState.auctionStatus!)
    }

    static isAuctionInProgress(auctionState: AuctionState) {
        return auctionState && [AuctionStatus.InProgress].includes(auctionState.auctionStatus!)
    }

    static isAuctionFinished(auctionState: AuctionState) {
        return auctionState && [AuctionStatus.Finished].includes(auctionState.auctionStatus!)
    }

    static isAuctionClosed(auctionState: AuctionState) {
        return auctionState && [AuctionStatus.Closed].includes(auctionState.auctionStatus!)
    }

    static isAuctionInProgressOrPaused(auctionState: AuctionState) {
        return AuctionHelper.isAuctionInProgress(auctionState) || AuctionHelper.isAuctionPaused(auctionState)
    }

    static hasUpcomingLot(auctionState: AuctionState) {
        return AuctionHelper.getFirstUpcomingLot(auctionState)
    }

    static getFirstUpcomingLot(auctionState: AuctionState) {
        return auctionState.lotsList ? auctionState.lotsList.find(l => LotHelper.isActive(l) && l.id !== auctionState.currentLotId && !l.parentId) : null
    }

    static getUpcomingLotById(auctionState: AuctionState, lotId) {
        return auctionState.lotsList ? auctionState.lotsList.find(l => l.id === Number(lotId || 0) && LotHelper.isActive(l) && l.id !== auctionState.currentLotId) : null
    }

    static getLastAddedUpcomingLot(auctionState: AuctionState) {
        if (Array.isArray(auctionState.lotsList) && auctionState.lotsList.length) {
            return auctionState.lotsList.filter(l => LotHelper.isActive(l)).sort((a, b) => (a.id! > b.id! ? -1 : b.id! > a.id! ? 1 : 0))[0]
        }
        return null
    }

    static getUpcomingLotsForManagement(auctionState: AuctionState) {
        return auctionState.lotsList ? auctionState.lotsList.filter(l => LotHelper.isUpcoming(l) && l.id !== auctionState.currentLotId) : []
    }

    static getParentPickLotsForAuction(auctionState: AuctionState) {
        return auctionState.lotsList!.filter(l => LotHelper.isPick(l) && LotHelper.isUpcoming(l))
    }

    static getUpcomingLotsForLotSelection(auctionState: AuctionState) {
        return auctionState.lotsList ? auctionState.lotsList.filter(l => LotHelper.isActive(l) && l.id !== auctionState.currentLotId && !l.parentId) : []
    }

    static getFinishedLots(auctionState: AuctionState) {
        return auctionState.lotsList ? auctionState.lotsList.filter(l => LotHelper.isLotClosed(l)) : []
    }

    static isSaleyardAuctionFormat(format) {
        return format === AUCTION_FORMAT.SALEYARD
    }

    static isSequentialAuctionFormat(format) {
        return format === AUCTION_FORMAT.SEQ
    }

    static isHybridAuctionFormat(format) {
        return format === AUCTION_FORMAT.HYBRID
    }

    static hasBidsForLot(auctionState: AuctionState) {
        return auctionState.uniqueBiddersList && auctionState.uniqueBiddersList.length
    }

    static hasNoneWithdrawnBidsForLot(auctionState: AuctionState) {
        return AuctionHelper.getOnlyNoneWithdrawnBidsForLot(auctionState).length
    }

    static getBidsForLot(auctionState: AuctionState) {
        var latestBidList = auctionState.bidList
            ? auctionState.bidList.filter(b => b.lotId === auctionState.currentLotId && b.biddingRun === auctionState.getCurrentLot()!.biddingRun)
            : []

        // Update the unique bidders list to be used by the offer screen when altering an offer
        if (latestBidList && latestBidList.length === 0) auctionState.uniqueBiddersList = []

        latestBidList.map((item: Bid) => {
            if (auctionState.uniqueBiddersList && !auctionState.uniqueBiddersList.some(bidder => bidder.bidderId === item.bidderId)) {
                auctionState.uniqueBiddersList.push({
                    bidderNumber: item.bidderNumber,
                    bidUser: item.bidUser,
                    bidderId: item.bidderId,
                    bidderName: item.bidderName,
                })
            }
        })

        auctionState.bidList = latestBidList.slice(0, 20)
        return auctionState.bidList
    }

    static getOnlyNoneWithdrawnBidsForLot(auctionState: AuctionState) {
        return auctionState.bidList.filter(b => !b.withdrawn)
    }

    static getBidderNameByBidderId(bidList: Bidder[], bidderId: number, floorBidderId: number, floorBidderNumber: number) {
        let bid = AuctionHelper.getBidderById(bidList, bidderId, floorBidderId, floorBidderNumber)
        return objectPath.get(bid, 'bidderName', null)
    }

    static getBiderNumberByBidderId(bidList: Bidder[], bidderId: number, floorBidderId: number, floorBidderNumber: number) {
        let bid = AuctionHelper.getBidderById(bidList, bidderId, floorBidderId, floorBidderNumber)
        return objectPath.get(bid, 'bidderNumber', null)
    }

    static getBidderById(bidList: any[], bidderId: number, floorBidderId: number, floorBidderNumber: number, mockFloorBidder: boolean = true) {
        let bid = null
        if (bidList) {
            bid = bidList.find(b => b.bidderId === bidderId)
        }
        if (!bid && mockFloorBidder && bidderId === floorBidderId) {
            //mock bid from floorbidder
            return {
                bidderId: floorBidderId,
                bidderName: 'Floor bidder', //TODO
                bidderNumber: floorBidderNumber,
            }
        }
        return bid ? bid : null
    }

    static canAddLots(auctionState: AuctionState) {
        return (
            auctionState &&
            [
                AuctionStatus.Draft,
                AuctionStatus.Pending,
                AuctionStatus.InEdit,
                AuctionStatus.Finalised,
                AuctionStatus.Ready,
                AuctionStatus.InProgress,
                AuctionStatus.Finished,
            ].includes(auctionState.auctionStatus!)
        )
    }

    /**
     * Approved bidder may view transport details (Ready / InProgress / Paused / Finalised / Finished).
     */
    static canViewBidderDetails(status: AuctionStatus | number | null | undefined): boolean {
        return AuctionHelper.canChangeBidderDetails(status) || status === AuctionStatus.Finished
    }

    /**
     * Mirrors Core API AuctionStatusExtensions.CanChangeBidderDetails
     * (Ready / InProgress / Paused / Finalised). Finished is read-only.
     */
    static canChangeBidderDetails(status: AuctionStatus | number | null | undefined): boolean {
        return (
            status === AuctionStatus.Ready ||
            status === AuctionStatus.InProgress ||
            status === AuctionStatus.Paused ||
            status === AuctionStatus.Finalised
        )
    }

    static getNextLot(model: AuctionModel, LotId: number) {
        return model.nextLots.find(x => x.id === LotId)
    }

    static isLotCurrentById(model: AuctionModel, LotId: number) {
        return model.currentLot && LotId === model.currentLot.id
    }

    static hasAnyUpcomingPicks(auctionState: AuctionState) {
        return AuctionHelper.getUpcomingLotsForManagement(auctionState).findIndex(x => LotHelper.isPick(x)) !== -1
    }

    static hasAnyUpcomingRunouts(auctionState: AuctionState) {
        return AuctionHelper.getUpcomingLotsForManagement(auctionState).findIndex(x => LotHelper.isRunout(x)) !== -1
    }

    static isCurrentLotParentOfLot(auctionState: AuctionState, lot) {
        return lot && lot.parentId && auctionState && lot.parentId === auctionState.currentLotId
    }
}
